import { useSearchParams } from "react-router-dom";
import { deepParam } from "../../utils/deepLink";
import countriesCode from "i18n-iso-countries";
import enCountries from "i18n-iso-countries/langs/en.json";
import React, { useEffect, useState , useRef } from "react";
import PageSeo from "../../components/seo/PageSeo";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import {
  appPlaceRent,
  getRentList,
  getRentListActive,
  cancelRentOrder,
  rentCountriesCall,
} from "../../calls";
import "./rent.css";
import Flag from "../../components/Flag";
import { toast } from "../../components/toast/toast";
import { countryName, fmtDate, fmtTime } from "../../utils/i18nFormat";

countriesCode.registerLocale(enCountries);

/* RENT — design "Signal" 4d */

// Durées supportées par le provider de location (jours).
const DAYS = [1, 3, 7, 14, 30];

// Drapeau + indicatif par pays (le backend renvoie le nom, pas le code ISO).
// `label` = nom anglais : repli et recherche ; l'affichage passe par countryName()
// (nom dans la langue du site, voir labelFor).
const COUNTRY_META = {
  usa: { code: "US", label: "United States", prefix: "+1" },
  england: { code: "GB", label: "United Kingdom", prefix: "+44" },
  indonesia: { code: "ID", label: "Indonesia", prefix: "+62" },
  malaysia: { code: "MY", label: "Malaysia", prefix: "+60" },
  estonia: { code: "EE", label: "Estonia", prefix: "+372" },
  greece: { code: "GR", label: "Greece", prefix: "+30" },
  netherlands: { code: "NL", label: "Netherlands", prefix: "+31" },
  poland: { code: "PL", label: "Poland", prefix: "+48" },
  brazil: { code: "BR", label: "Brazil", prefix: "+55" },
  ukraine: { code: "UA", label: "Ukraine", prefix: "+380" },
  italy: { code: "IT", label: "Italy", prefix: "+39" },
  hungary: { code: "HU", label: "Hungary", prefix: "+36" },
  france: { code: "FR", label: "France", prefix: "+33" },
  germany: { code: "DE", label: "Germany", prefix: "+49" },
  spain: { code: "ES", label: "Spain", prefix: "+34" },
  canada: { code: "CA", label: "Canada", prefix: "+1" },
  india: { code: "IN", label: "India", prefix: "+91" },
  philippines: { code: "PH", label: "Philippines", prefix: "+63" },
  thailand: { code: "TH", label: "Thailand", prefix: "+66" },
  vietnam: { code: "VN", label: "Vietnam", prefix: "+84" },
  turkey: { code: "TR", label: "Turkey", prefix: "+90" },
  romania: { code: "RO", label: "Romania", prefix: "+40" },
  portugal: { code: "PT", label: "Portugal", prefix: "+351" },
  ireland: { code: "IE", label: "Ireland", prefix: "+353" },
  sweden: { code: "SE", label: "Sweden", prefix: "+46" },
  bulgaria: { code: "BG", label: "Bulgaria", prefix: "+359" },
  czech: { code: "CZ", label: "Czech Republic", prefix: "+420" },
  croatia: { code: "HR", label: "Croatia", prefix: "+385" },
  austria: { code: "AT", label: "Austria", prefix: "+43" },
  slovakia: { code: "SK", label: "Slovakia", prefix: "+421" },
  belgium: { code: "BE", label: "Belgium", prefix: "+32" },
  latvia: { code: "LV", label: "Latvia", prefix: "+371" },
  lithuania: { code: "LT", label: "Lithuania", prefix: "+370" },
};
const metaFor = (name) => {
  const key = String(name || "").toLowerCase();
  if (COUNTRY_META[key]) return COUNTRY_META[key];
  // Filet : le provider peut livrer de nouveaux pays a tout moment. On derive
  // le code ISO du nom plutot que de laisser un drapeau vide.
  const label = key.replace(/^\w/, (m) => m.toUpperCase());
  let code = "";
  try {
    code = countriesCode.getAlpha2Code(label, "en") || "";
  } catch (e) {
    code = "";
  }
  return { code, label, prefix: "" };
};

// Code d'erreur stable du serveur (orders/rent_views.py) -> texte traduit.
const RENT_ERR_KEYS = {
  NO_BALANCE: "signal.rent-t-balance-body",
  NO_NUMBERS: "signal.rent-t-failed-body",
  BAD_COUNTRY: "signal.rent-err-country",
  BAD_SERVICE: "signal.rent-err-service",
  CANCEL_REFUSED: "signal.rent-err-cancel-refused",
};

const Rent = (props) => {
  const { t, i18n } = useTranslation();
  // Nom du pays dans la langue du site (repli : nom anglais / slug)
  const labelFor = (name) => {
    const m = metaFor(name);
    return countryName(m.code, m.label, i18n.language);
  };
  const daysLabel = (n) => `${n} ${n > 1 ? t("signal.days") : t("signal.day")}`;
  const { setBalance, isLoggedIn, setLoader, setShowModal } = props;

  const [rentList, setRentList] = useState([]);
  const [rentListActive, setRentListActive] = useState([]);
  const [currentNumber, setCurrentNumber] = useState(null);
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);   // pays réellement louables
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [countryQuery, setCountryQuery] = useState("");
  // Deep-link depuis la home SEO : /rent#country=usa&days=7 (ou l'ancien ?country=...)
  const [searchParams] = useSearchParams();
  const deepCountry = deepParam(searchParams, "country").toLowerCase();
  const deepDays = parseInt(deepParam(searchParams, "days"), 10);
  const deepIdx = DAYS.indexOf(deepDays);
  const [rangeIdx, setRangeIdx] = useState(deepIdx >= 0 ? deepIdx : 2);
  const deepCountryRef = useRef(deepCountry);
  const [daysAvail, setDaysAvail] = useState(null);

  const fetchAll = async () => {
    try {
      setLoader(false);
      const [hist, active] = await Promise.all([
        getRentList().catch(() => ({ data: [] })),
        getRentListActive().catch(() => ({ data: [] })),
      ]);
      setRentList(Array.isArray(hist.data) ? hist.data : []);
      setRentListActive(Array.isArray(active.data) ? active.data : []);
      setCurrentNumber((active.data && active.data[0]) || null);
    } finally {
      setLoader(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn) fetchAll();
    else setLoader(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Durees qui ont reellement des pays chez le provider (certaines sont
  // vides) : null tant que la mesure n'a pas abouti.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled(DAYS.map((d) => rentCountriesCall(d))).then((results) => {
      if (cancelled) return;
      setDaysAvail(
        results.map((r) => {
          if (r.status !== "fulfilled") return true;
          const list = (r.value && r.value.data && r.value.data.countries) || [];
          return list.length > 0;
        })
      );
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Pays louables + prix REELS pour la duree choisie (le stock varie).
  useEffect(() => {
    // catalogue public : on affiche prix et dispo meme deconnecte (le login
    // se declenche a l'achat) — c'est la cible des liens Full Rent de la home.
    let cancelled = false;
    setLoadingCountries(true);
    rentCountriesCall(DAYS[rangeIdx])
      .then((res) => {
        if (cancelled) return;
        const list = (res.data && res.data.countries) || [];
        setCountries(list);
        setCountry((prev) => {
          const wanted = deepCountryRef.current;
          if (wanted) {
            const hit = list.find(
              // NB: l'API renvoie `code` en numerique (id interne provider) ->
              // String() obligatoire, sinon TypeError au 1er rendu.
              (c) => String(c.country || "").toLowerCase() === wanted ||
                     String(c.code || "").toLowerCase() === wanted
            );
            if (list.length > 0) deepCountryRef.current = "";  // one-shot
            if (hit) return hit.country;
          }
          return list.some((c) => c.country === prev)
            ? prev
            : (list[0] ? list[0].country : "");
        });
      })
      .catch(() => { if (!cancelled) setCountries([]); })
      .finally(() => { if (!cancelled) setLoadingCountries(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, rangeIdx]);

  // Tuiles affichees : uniquement les durees qui ont des pays (si aucune
  // mesure ou aucune disponible, on garde tout).
  const shownIdx = daysAvail === null
    ? DAYS.map((_, i) => i)
    : daysAvail.includes(true)
      ? daysAvail.map((ok, i) => (ok ? i : -1)).filter((i) => i >= 0)
      : DAYS.map((_, i) => i);

  // La duree choisie n'a pas de pays : bascule sur la premiere disponible
  // (preference 7, 3, 1, 14, 30 jours). Un lien ?days= valide est garde.
  useEffect(() => {
    if (daysAvail === null || daysAvail[rangeIdx] !== false) return;
    const next = [2, 1, 0, 3, 4].find((i) => i < daysAvail.length && daysAvail[i]);
    if (next !== undefined) setRangeIdx(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAvail]);

  const selected = countries.find((c) => c.country === country) || null;

  const handleRent = () => {
    if (!isLoggedIn) { setShowModal(true); return; }
    toast("info", t("signal.rent-t-setting"), `${labelFor(country)} · ${daysLabel(DAYS[rangeIdx])}`, { ttl: 2500 });
    appPlaceRent(DAYS[rangeIdx], country)
      .then((response) => {
        fetchAll();
        if (response.data && response.data.amount != null) setBalance(response.data.amount);
        toast("ok", t("signal.rent-t-started"), t("signal.rent-t-started-body"));
      })
      .catch((err) => {
        const status = err?.response?.status;
        // Le serveur renvoie un code stable (NO_BALANCE, NO_NUMBERS...) : on
        // affiche le texte traduit correspondant, jamais son message anglais.
        const code = err?.response?.data?.code;
        const body = RENT_ERR_KEYS[code]
          ? t(RENT_ERR_KEYS[code])
          : status === 406
            ? t("signal.rent-t-balance-body")
            : status && status < 500
              ? t("signal.rent-t-failed-body")
              : t("signal.t-try-again");
        if (status === 406) {
          toast("err", t("signal.t-no-balance"), body);
        } else {
          toast("err", t("signal.rent-t-failed"), body);
        }
      });
  };

  const handleCancel = (id) => {
    cancelRentOrder(id);
    fetchAll();
  };

  const codeFor = (c) =>
    metaFor(c).code;

  // Liste filtree affichee dans la box scrollable
  const visibleCountries = countries.filter((c) => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return true;
    const m = metaFor(c.country);
    return (
      (m.label || "").toLowerCase().includes(q) ||
      labelFor(c.country).toLowerCase().includes(q) ||
      (c.country || "").toLowerCase().includes(q) ||
      (m.code || "").toLowerCase().includes(q)
    );
  });

  const inboxMessages = currentNumber
    ? rentList
        .filter((m) => m.number === currentNumber.number)
        .flatMap((m) =>
          (m.sms_codes || []).map((sms) => ({
            from: m.country ? labelFor(m.country) : "SMS",
            time: fmtTime(m.created_at, undefined, i18n.language),
            body: sms,
          }))
        )
    : [];

  return (
    <>
      <PageSeo path="/rent" />
      <Helmet>
        <title>{t("signal.seo-rent-title")}</title>
        <meta name="robots" content="index, follow" />
        <meta
          name="description"
          content={t("signal.seo-rent-desc")}
        />
      </Helmet>

      <div className="sg-page">
        <div className="sg-cols">
          {/* Colonne gauche */}
          <div className="sg-col" style={{ flex: "1 1 500px" }}>
            <div className="sg-panel">
              <h1 className="sg-h1" style={{ marginBottom: 4 }}>{t("signal.rent-title")}</h1>
              <p className="sg-sub" style={{ marginBottom: 16 }}>
                {t("signal.rent-sub")}
              </p>

              <div className="sg-boxhead">
                <div className="sg-kicker">{t("signal.rent-step-country")}</div>
                {!loadingCountries && countries.length > 0 && (
                  <span className="sg-boxhead__count">
                    {visibleCountries.length}/{countries.length}
                  </span>
                )}
              </div>
              {!loadingCountries && countries.length > 6 && (
                <input
                  className="sg-boxsearch"
                  type="search"
                  value={countryQuery}
                  onChange={(e) => setCountryQuery(e.target.value)}
                  placeholder={t("signal.search-country")}
                  aria-label={t("signal.search-country")}
                />
              )}
              <div className="sg-scrollbox" style={{ marginBottom: 16 }}>
                {loadingCountries ? (
                  <div className="sg-empty" style={{ border: "none" }}>{t("signal.loading-countries")}</div>
                ) : countries.length === 0 ? (
                  <div className="sg-empty" style={{ border: "none" }}>
                    {t("signal.rent-t-failed-body")}
                  </div>
                ) : visibleCountries.length === 0 ? (
                  <div className="sg-empty" style={{ border: "none" }}>—</div>
                ) : (
                  visibleCountries.map((c) => {
                    const m = metaFor(c.country);
                    return (
                      <button
                        key={c.country}
                        className={`sg-countryrow ${country === c.country ? "is-on" : ""}`}
                        onClick={() => setCountry(c.country)}
                      >
                        <span className="sg-countryrow__flag"><Flag code={m.code} /></span>
                        <span className="sg-countryrow__name">{countryName(m.code, m.label, i18n.language)}</span>
                        <span className="sg-td--mono" style={{ color: "var(--color-muted)", marginLeft: "auto" }}>
                          ${Number(c.price).toFixed(2)}
                        </span>
                        <span className="sg-td--mono" style={{ color: "var(--color-muted)", fontSize: 11, marginLeft: 8 }}>
                          {t("signal.rent-left", { count: c.quantity })}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="sg-kicker" style={{ marginBottom: 10 }}>{t("signal.rent-step-duration")}</div>
              <div className={`sg-tiles sg-tiles--${shownIdx.length}`} style={{ marginBottom: 16 }}>
                {shownIdx.map((i) => {
                  const d = DAYS[i];
                  return (
                    <button
                      key={d}
                      className={`sg-tile sg-tile--center ${rangeIdx === i ? "is-on" : ""}`}
                      onClick={() => setRangeIdx(i)}
                    >
                      <span style={{ font: "700 15px/1 var(--font-mono)", color: "var(--color-ink)" }}>{d}</span>
                      <span className="sg-tile__s">{d > 1 ? t("signal.days") : t("signal.day")}</span>

                    </button>
                  );
                })}
              </div>

              <div
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, borderTop: "1px solid var(--color-line)", paddingTop: 14, flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <span style={{ font: "400 11px var(--font-body)", color: "var(--color-muted)" }}>
                    {t("signal.rent-total", { n: DAYS[rangeIdx], unit: DAYS[rangeIdx] > 1 ? t("signal.days") : t("signal.day") })}
                  </span>
                  <span style={{ font: "700 22px/1 var(--font-mono)", color: "var(--color-ink)" }}>
                    {selected ? `$${Number(selected.price).toFixed(2)}` : "—"}
                  </span>
                </div>
                <button className="sg-btn sg-btn--primary" onClick={handleRent} disabled={!selected}>
                  {t("signal.rent-cta")}
                </button>
              </div>
            </div>

            <div className="sg-panel">
              <div className="sg-head sg-head--center" style={{ marginBottom: 12 }}>
                <h2 className="sg-h2">{t("signal.rent-history")}</h2>
                <span className="sg-right" style={{ font: "400 12px var(--font-body)", color: "var(--color-muted)" }}>
                  {t("signal.rent-count", { count: rentList.length })}
                </span>
              </div>
              <div className="sg-table sg-scroll-y" style={{ "--cols": "90px minmax(130px,1fr) 60px 110px 90px" }}>
                <div className="sg-tr sg-tr--head">
                  <span>{t("overview.country")}</span><span>{t("overview.number")}</span><span>{t("signal.rent-days")}</span><span>{t("signal.rent-expires")}</span><span>{t("overview.status")}</span>
                </div>
                {rentList.length === 0 ? (
                  <div className="sg-empty" style={{ border: "none", borderRadius: 0 }}>
                    {t("signal.rent-none-body")}
                  </div>
                ) : (
                  rentList.map((item, index) => (
                    <div key={index} className="sg-tr">
                      <span className="sg-td--ellip"><Flag code={codeFor(item.country)} /> {labelFor(item.country)}</span>
                      <span className="sg-td--num">{item.number}</span>
                      <span className="sg-td--mono">{item.time}</span>
                      <span className="sg-td--mono">{item.expired_at ? fmtDate(item.expired_at, undefined, i18n.language) : "—"}</span>
                      <span className="sg-status sg-status--mut">{t("signal.rent-ended")}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Colonne droite : Inbox */}
          <div className="sg-panel" style={{ flex: "1 1 380px", minWidth: 0 }}>
            <div className="sg-head sg-head--center" style={{ marginBottom: 6 }}>
              <h2 className="sg-h2">{t("signal.rent-inbox")}</h2>
              {currentNumber && (
                <span className="sg-numtag">{currentNumber.number}</span>
              )}
              {currentNumber && (
                <button className="sg-copy sg-right" onClick={() => handleCancel(currentNumber.id)}>
                  {t("signal.rent-cancel")}
                </button>
              )}
            </div>
            <p className="sg-sub" style={{ color: "var(--color-muted)", marginBottom: 14 }}>
              {t("signal.rent-inbox-sub")}
            </p>
            <div className="sg-scroll-y sg-scroll-y--inbox" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {rentListActive.length === 0 ? (
                <div className="sg-empty">
                  <b style={{ display: "block", color: "var(--color-ink)", marginBottom: 4 }}>{t("signal.rent-none-title")}</b>
                  {t("signal.rent-none-body")}
                </div>
              ) : (
                <>
                  {inboxMessages.map((m, i) => (
                    <div key={i} className="sg-msg">
                      <div className="sg-msg__head">
                        <span className="sg-msg__from">{m.from}</span>
                        <span className="sg-msg__time">{m.time}</span>
                      </div>
                      <p className="sg-msg__body">
                        <span className="sg-msg__code">{m.body}</span>
                      </p>
                    </div>
                  ))}
                  <div className="sg-empty">{t("signal.rent-waiting")}</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rent;
